import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Close, EventRepeat, HourglassTop, PauseCircle, PlayCircle, Bolt } from '@mui/icons-material';
import { Grid } from '../components/common/TypedGrid';
import {
  FilterTabs,
  PageHeader,
  ResponsiveTable,
  ResponsiveColumn,
  SearchField,
  SectionCard,
  StatCard,
  StatusChip,
} from '../components/common';
import { useAdmin } from '../contexts/AdminContext';
import agencyAdminService, { AdminSubscription, callableError } from '../services/agencyAdminService';

type Filter = 'requested' | 'active' | 'paused' | 'cancelled' | 'all';

const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const rupees = (n?: number) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;

/** Today's date in India as YYYY-MM-DD — the calendar subscriptions run on. */
const istToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());

/**
 * The server marks a subscription 'paused' as soon as a pause is booked, even
 * one starting later, and flips it back the night after it ends. Lists go by
 * the dates: paused only while today is inside the pause.
 */
function effectiveStatus(s: AdminSubscription, today: string): AdminSubscription['status'] {
  if (s.status !== 'paused') return s.status;
  return s.pause && s.pause.from <= today && today <= s.pause.until ? 'paused' : 'active';
}

function scheduleText(s: AdminSubscription): string {
  const t = s.schedule.startTime;
  if (s.schedule.frequency === 'daily') return `Every day · ${t}`;
  if (s.schedule.frequency === 'alternate') return `Every other day · ${t}`;
  // Same wording as the app: three or more days in a row read as a range.
  const days = [...(s.schedule.daysOfWeek || [])].sort((x, y) => x - y);
  const parts: string[] = [];
  for (let i = 0; i < days.length; ) {
    let j = i;
    while (j + 1 < days.length && days[j + 1] === days[j] + 1) j++;
    parts.push(j - i >= 2 ? `${DAY[days[i]]}–${DAY[days[j]]}` : days.slice(i, j + 1).map((d) => DAY[d]).join(', '));
    i = j + 1;
  }
  return `${days.length === 7 ? 'Every day' : parts.join(', ')} · ${t}`;
}

/**
 * Recurring bookings across the platform: requests waiting for a provider,
 * running and paused subscriptions, and ended ones. Admins can cancel on a
 * customer's behalf (which releases any reserved money) and, as super admin,
 * run the visit generator on demand.
 */
const Subscriptions: React.FC = () => {
  const { effectiveCityId, isSuperAdmin } = useAdmin();
  const [rows, setRows] = useState<AdminSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('active');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<AdminSubscription | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await agencyAdminService.getSubscriptions(effectiveCityId));
    } catch (e) {
      setToast({ msg: callableError(e), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [effectiveCityId]);

  useEffect(() => {
    load();
  }, [load]);

  const today = istToday();
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    rows.forEach((r) => {
      const st = effectiveStatus(r, today);
      c[st] = (c[st] || 0) + 1;
    });
    return c;
  }, [rows, today]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (filter === 'all' || effectiveStatus(r, today) === filter) &&
        (!q ||
          r.serviceName.toLowerCase().includes(q) ||
          (r.customerInfo?.name || '').toLowerCase().includes(q) ||
          (r.provider?.name || '').toLowerCase().includes(q))
    );
  }, [rows, filter, search, today]);

  const monthlyValue = useMemo(
    () =>
      rows
        .filter((r) => effectiveStatus(r, today) === 'active')
        .reduce((sum, r) => {
          const perWeek = r.schedule.frequency === 'daily' ? 7 : r.schedule.frequency === 'alternate' ? 3.5 : (r.schedule.daysOfWeek || []).length;
          return sum + r.pricePerVisit * perWeek * 4.3;
        }, 0),
    [rows, today]
  );

  const cancel = async () => {
    if (!selected || !reason.trim()) return;
    setBusy(true);
    try {
      const r = await agencyAdminService.cancelSubscription(selected.id, reason.trim());
      setToast({ msg: `Cancelled${r.visitsReleased ? ` — ${r.visitsReleased} upcoming visit(s) released` : ''}`, severity: 'success' });
      setCancelOpen(false);
      setReason('');
      setSelected(null);
      await load();
    } catch (e) {
      setToast({ msg: callableError(e), severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const runScheduler = async () => {
    setBusy(true);
    try {
      const r = await agencyAdminService.runScheduler();
      const parts = Object.entries(r.results).map(([k, v]) => `${v} ${k.replace(/_/g, ' ')}`);
      setToast({ msg: `Visits for ${r.target}: ${parts.join(', ') || 'none due'}${r.missedClosed ? `; ${r.missedClosed} missed closed` : ''}`, severity: 'info' });
    } catch (e) {
      setToast({ msg: callableError(e), severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const columns: ResponsiveColumn<AdminSubscription>[] = [
    {
      key: 'service',
      header: 'Subscription',
      mobile: 'title',
      render: (r) => (
        <Box>
          <Typography fontWeight={700}>{r.serviceName}</Typography>
          <Typography variant="caption" color="text.secondary">{scheduleText(r)}</Typography>
        </Box>
      ),
    },
    { key: 'customer', header: 'Customer', mobile: 'subtitle', render: (r) => r.customerInfo?.name || '—' },
    {
      key: 'provider',
      header: 'Provider',
      mobile: 'body',
      render: (r) =>
        r.provider ? (
          <Box>
            <Typography variant="body2">{r.provider.name}</Typography>
            <Typography variant="caption" color="text.secondary">{r.provider.type === 'agency' ? `Agency · ${r.assigneeName || '—'}` : 'Individual'}</Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">Waiting</Typography>
        ),
    },
    { key: 'price', header: 'Per visit', align: 'right', mobile: 'body', render: (r) => rupees(r.pricePerVisit) },
    { key: 'done', header: 'Visits done', align: 'center', mobile: 'body', render: (r) => r.visitsCompleted || 0 },
    {
      key: 'status',
      header: 'Status',
      mobile: 'actions',
      render: (r) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <StatusChip status={effectiveStatus(r, today)} />
          {r.issue?.reason === 'insufficient_balance' && (r.status === 'active' || r.status === 'paused') && (
            <Typography variant="caption" color="error.main" title={`Wallet short for ${r.issue.date}`}>●</Typography>
          )}
        </Stack>
      ),
    },
  ];

  const s = selected;

  return (
    <Box>
      <PageHeader
        title="Subscriptions"
        subtitle="Recurring visits booked by customers — daily, alternate days or chosen weekdays."
        actions={
          isSuperAdmin ? (
            <Button variant="outlined" startIcon={busy ? <CircularProgress size={16} /> : <Bolt />} onClick={runScheduler} disabled={busy}>
              Generate tomorrow’s visits
            </Button>
          ) : undefined
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}><StatCard label="Waiting for a provider" value={counts.requested || 0} icon={<HourglassTop />} tone="warning" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Active" value={counts.active || 0} icon={<PlayCircle />} tone="success" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Paused" value={counts.paused || 0} icon={<PauseCircle />} tone="info" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Monthly value (active)" value={rupees(monthlyValue)} icon={<EventRepeat />} tone="primary" /></Grid>
      </Grid>

      <SectionCard disablePadding>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ p: 2 }}>
          <FilterTabs<Filter>
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'requested', label: 'Requests', count: counts.requested },
              { value: 'active', label: 'Active', count: counts.active },
              { value: 'paused', label: 'Paused', count: counts.paused },
              { value: 'cancelled', label: 'Cancelled', count: counts.cancelled },
              { value: 'all', label: 'All', count: rows.length },
            ]}
          />
          <SearchField value={search} onChange={setSearch} placeholder="Search service, customer or provider" />
        </Stack>
        <ResponsiveTable<AdminSubscription>
          columns={columns}
          rows={visible}
          rowKey={(r) => r.id}
          loading={loading}
          onRowClick={setSelected}
          emptyTitle="No subscriptions here"
          emptyDescription="Customers subscribe from any service an admin has enabled for subscriptions (Services & Pricing)."
        />
      </SectionCard>

      <Drawer anchor="right" open={!!s} onClose={() => setSelected(null)} PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}>
        {s && (
          <Box sx={{ p: 3 }}>
            <Stack direction="row" alignItems="flex-start" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={800}>{s.serviceName}</Typography>
                <Typography variant="body2" color="text.secondary">{scheduleText(s)} · {s.schedule.durationMinutes} min</Typography>
                <Box sx={{ mt: 1 }}><StatusChip status={effectiveStatus(s, today)} /></Box>
              </Box>
              <IconButton onClick={() => setSelected(null)}><Close /></IconButton>
            </Stack>

            {s.issue?.reason === 'insufficient_balance' && (s.status === 'active' || s.status === 'paused') && (
              <Alert severity="warning" sx={{ mt: 2 }}>The {s.issue.date} visit wasn’t created — the customer’s wallet couldn’t cover it.</Alert>
            )}

            <Divider sx={{ my: 2.5 }} />
            {[
              ['Customer', `${s.customerInfo?.name || '—'}${s.customerInfo?.phone ? ` · ${s.customerInfo.phone}` : ''}`],
              ['Address', s.location?.address?.fullAddress || '—'],
              ['Provider', s.provider ? `${s.provider.name} (${s.provider.type === 'agency' ? 'agency' : 'individual'})` : 'Waiting for a provider'],
              ['Done by', s.assigneeName || '—'],
              ['Price', `${rupees(s.pricePerVisit)} per visit`],
              ['Started', s.startDate],
              ['Ends', s.endDate || 'Until cancelled'],
              ['Pause', s.pause && s.status === 'paused' && s.pause.until >= today ? `${s.pause.from} → ${s.pause.until}` : '—'],
              ['Skipped days', s.skipDates?.length ? s.skipDates.join(', ') : '—'],
              ['Visits done', String(s.visitsCompleted || 0)],
            ].map(([k, v]) => (
              <Stack key={k} direction="row" spacing={2} sx={{ py: 0.75 }}>
                <Typography variant="body2" color="text.secondary" sx={{ width: 110, flexShrink: 0 }}>{k}</Typography>
                <Typography variant="body2">{v}</Typography>
              </Stack>
            ))}

            {s.status !== 'cancelled' && s.status !== 'declined' && (
              <>
                <Divider sx={{ my: 2.5 }} />
                <Button variant="outlined" color="error" fullWidth onClick={() => setCancelOpen(true)}>
                  Cancel on the customer’s behalf
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Upcoming visits are cancelled and their reserved money goes back to the customer’s wallet.
                </Typography>
              </>
            )}
          </Box>
        )}
      </Drawer>

      <Dialog open={cancelOpen} onClose={() => !busy && setCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel this subscription?</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth multiline minRows={2} label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)} disabled={busy}>Keep</Button>
          <Button variant="contained" color="error" onClick={cancel} disabled={busy || !reason.trim()}>
            {busy ? <CircularProgress size={20} color="inherit" /> : 'Cancel subscription'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default Subscriptions;
